import React, { useState } from 'react';
import { Menu, X, ChevronDown, Globe, Wallet, Moon, Sun } from 'lucide-react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useAppSettings, languages } from '../lib/i18n';
import { currencies } from '../lib/currency';

const pageLinks = [
  { to: '/', label: 'Home', end: true },
  { to: '/about', label: 'About' },
  { to: '/faq', label: 'FAQ' },
  { to: '/privacy', label: 'Privacy' },
  { to: '/contact', label: 'Contact' },
];

export default function Header() {
  const { t, lang, setLang, currency, setCurrency, theme, setTheme } = useAppSettings();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isDark = theme === 'dark';
  const toggleTheme = () => setTheme(isDark ? 'light' : 'dark');

  const linkClass = ({ isActive }) =>
    `text-sm font-medium whitespace-nowrap transition-colors ${
      isActive ? 'text-[#0176D3]' : 'text-slate-700 dark:text-slate-300 hover:text-[#0176D3]'
    }`;

  return (
    <header className="sticky top-0 z-40 w-full bg-white dark:bg-[#101B33] border-b border-slate-200 dark:border-[#1E2A44]">
      <div className="emi-wrap">
        <div className="h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="h-9 w-9 rounded-lg bg-[#0176D3] flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-lg">E</span>
            </div>
            <span className="text-[#032D60] dark:text-white font-semibold text-lg tracking-tight">{t('app_name')}</span>
          </Link>

          <nav className="hidden md:flex items-center gap-4 lg:gap-6" key={location.pathname}>
            {pageLinks.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} className={linkClass}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-end gap-3">
            <div className="flex flex-col items-center gap-1">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">
                {isDark ? 'Light Mode' : 'Dark Mode'}
              </span>
              <button
                onClick={toggleTheme}
                className="btn-3d h-9 w-9 rounded-md border border-slate-200 dark:border-[#1E2A44] bg-white dark:bg-[#0F1B36] hover:border-[#0176D3] hover:text-[#0176D3] text-slate-700 dark:text-slate-300 flex items-center justify-center"
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                data-testid="theme-toggle"
              >
                {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
            </div>

            <div className="flex flex-col items-center gap-1">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('currency')}</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="btn-3d h-9 px-3 rounded-md border border-slate-200 dark:border-[#1E2A44] bg-white dark:bg-[#0F1B36] hover:border-[#0176D3] hover:text-[#0176D3] text-slate-700 dark:text-slate-300 text-xs font-semibold flex items-center gap-1.5"
                    data-testid="currency-trigger"
                  >
                    <Wallet className="h-3.5 w-3.5" />
                    {currencies[currency].symbol} {currency}
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  <DropdownMenuLabel className="text-xs text-slate-500">{t('currency')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {Object.values(currencies).map((c) => (
                    <DropdownMenuItem
                      key={c.code}
                      onSelect={() => setCurrency(c.code)}
                      className={`text-sm cursor-pointer ${currency === c.code ? 'text-[#0176D3] font-semibold' : ''}`}
                      data-testid={`currency-option-${c.code}`}
                    >
                      <span className="w-6">{c.symbol}</span>
                      <span className="flex-1">{c.code}</span>
                      <span className="text-xs text-slate-500">{c.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <div className="flex flex-col items-center gap-1">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400">{t('language')}</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="btn-3d h-9 px-3 rounded-md border border-slate-200 dark:border-[#1E2A44] bg-white dark:bg-[#0F1B36] hover:border-[#0176D3] hover:text-[#0176D3] text-slate-700 dark:text-slate-300 text-xs font-semibold flex items-center gap-1.5"
                    data-testid="language-trigger"
                  >
                    <Globe className="h-3.5 w-3.5" />
                    {languages[lang].flag}
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  <DropdownMenuLabel className="text-xs text-slate-500">{t('language')}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {Object.entries(languages).map(([code, meta]) => (
                    <DropdownMenuItem
                      key={code}
                      onSelect={() => setLang(code)}
                      className={`text-sm cursor-pointer ${lang === code ? 'text-[#0176D3] font-semibold' : ''}`}
                      data-testid={`language-option-${code}`}
                    >
                      <span className="w-8 text-xs font-bold">{meta.flag}</span>
                      <span className="flex-1">{meta.label}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <button
            className="md:hidden h-9 w-9 rounded-md hover:bg-slate-100 dark:hover:bg-[#0F1B36] flex items-center justify-center"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {mobileOpen && (
          <div className="md:hidden pb-4 space-y-3 border-t border-slate-100 dark:border-[#1E2A44] pt-3">
            <div className="flex gap-2">
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="flex-1 h-9 rounded-md border border-slate-200 dark:border-[#1E2A44] bg-white dark:bg-[#0F1B36] px-2 text-sm"
              >
                {Object.values(currencies).map((c) => (
                  <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
                ))}
              </select>
              <select
                value={lang}
                onChange={(e) => setLang(e.target.value)}
                className="flex-1 h-9 rounded-md border border-slate-200 dark:border-[#1E2A44] bg-white dark:bg-[#0F1B36] px-2 text-sm"
              >
                {Object.entries(languages).map(([code, meta]) => (
                  <option key={code} value={code}>{meta.label}</option>
                ))}
              </select>
              <button
                onClick={toggleTheme}
                className="btn-3d h-9 w-9 rounded-md border border-slate-200 dark:border-[#1E2A44] bg-white dark:bg-[#0F1B36] flex items-center justify-center"
                aria-label="Toggle theme"
              >
                {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {pageLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `text-center py-2 rounded-md border text-sm font-medium ${
                      isActive
                        ? 'bg-[#0176D3] border-[#0176D3] text-white'
                        : 'border-slate-200 text-slate-700 dark:text-slate-300'
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
